/**
 * Multiscount live price preview.
 *
 * Shows the bulk discount live as the shopper changes quantity. Reads the per-tier
 * discount % from the Multiscount table and applies it to the variant's price.
 * Does nothing if the table isn't on the page. Read-only — never touches the cart.
 *
 * The Add to Cart total is left to the theme's buy-buttons-pricing.js: it asks 
 * for a discounted line total via window.MultiscountTiered.lineTotalCents(). 
 */
(function () {
  'use strict';

  var CONFIG = {
    tableInstanceSelector: '[id^="multiscount-theme-extension-"]',
    sectionSelector: '.product__info-container',
    quantityInputSelector: 'form[data-type="add-to-cart-form"] input.quantity__input[name="quantity"]',
    quantityButtonsSelector: 'form[data-type="add-to-cart-form"] .quantity__button',
    variantIdSelector: 'form[data-type="add-to-cart-form"] input[name="id"].product-variant-id',
    priceRootSelector: '.product__block--price .f-price',
    buyButtonsDataSelector: 'script[data-buy-buttons-data]',
    selectedVariantSelector: 'script[data-selected-variant]',
    debounceMs: 120,
    reinitDebounceMs: 200,
    showOriginalStrikethrough: true,
    updateButtonTotal: true, // feed the discounted total to buy-buttons-pricing.js
    markerAttr: 'data-ms-live-price',
    retryDelaysMs: [0, 150, 400, 800, 1500, 2500, 4000] // table renders a moment after load
  };

  var controllers = [];

  function debounce(fn, wait) {
    var t;
    return function () {
      var ctx = this, args = arguments;
      clearTimeout(t);
      t = setTimeout(function () { fn.apply(ctx, args); }, wait);
    };
  }

  function moneyToCents(text) {
    if (!text) return null;
    var cleaned = String(text).replace(/[^0-9.]/g, '');
    if (cleaned === '' || cleaned === '.') return null;
    var value = parseFloat(cleaned);
    if (isNaN(value)) return null;
    return Math.round(value * 100);
  }

  function centsToMoney(cents) {
    return '$' + (cents / 100).toLocaleString('en-US', {
      minimumFractionDigits: 2,
      maximumFractionDigits: 2
    });
  }

  // Use the theme's formatter so our prices match the rest of the page.
  function formatMoney(cents) {
    try {
      if (window.FoxTheme && FoxTheme.Currency && FoxTheme.Currency.formatMoney) {
        return FoxTheme.Currency.formatMoney(cents, FoxTheme.settings && FoxTheme.settings.moneyFormat);
      }
    } catch (e) { /* fall through */ }
    return centsToMoney(cents);
  }

  // Only runs when we refresh (load, the bounded retries, variant change) — never in a
  // loop. checkVisibility covers display/visibility/opacity where supported.
  function isVisible(el) {
    if (typeof el.checkVisibility === 'function') {
      return el.checkVisibility({ visibilityProperty: true, opacityProperty: true, contentVisibilityAuto: true });
    }
    return el.offsetParent !== null;
  }

  // Newest table that's actually showing (the id increments on re-render). Visibility is
  // the eligibility signal: if the widget is hidden for this customer — however that's
  // done — we treat the bulk discount as not applying and stay out of the way.
  function getLatestTable(section) {
    var instances = (section || document).querySelectorAll(CONFIG.tableInstanceSelector);
    for (var i = instances.length - 1; i >= 0; i--) {
      var el = instances[i];
      if (el.querySelector('table') && isVisible(el)) return el;
    }
    return null;
  }

  // The theme's visually-hidden "Sale price" / "Regular price" labels, read from
  // the price markup so we can reuse them (& keep them translated).
  function getPriceLabels(root) {
    var out = { sale: '', regular: '' };
    var spans = root.querySelectorAll('.f-price__sale .visually-hidden--inline');
    if (spans[0]) out.sale = spans[0].textContent.trim();
    if (spans[1]) out.regular = spans[1].textContent.trim();
    if (!out.regular) {
      var reg = root.querySelector('.f-price__regular .visually-hidden--inline');
      if (reg) out.regular = reg.textContent.trim();
    }
    return out;
  }

  // -> [{ minQty, pct, discountedTotalCents }], lowest first.
  function parseTiers(tableInstance) {
    var rows = tableInstance.querySelectorAll('table tbody tr');
    var tiers = [];

    rows.forEach(function (row) {
      var cells = row.querySelectorAll('td');
      if (cells.length < 4) return;

      // Quantity column, falling back to the "Buy N+" label.
      var minQty = parseInt((cells[1].textContent || '').replace(/[^0-9]/g, ''), 10);
      if (!minQty) {
        var m = (cells[0].textContent || '').match(/(\d+)/);
        minQty = m ? parseInt(m[1], 10) : NaN;
      }
      if (!minQty || isNaN(minQty)) return;

      var pctMatch = (cells[2].textContent || '').match(/([\d.]+)\s*%/);
      var pct = pctMatch ? parseFloat(pctMatch[1]) : null;

      var priceEls = cells[3].querySelectorAll('h3');
      var discountedTotalCents = priceEls.length ? moneyToCents(priceEls[0].textContent) : null;

      tiers.push({ minQty: minQty, pct: pct, discountedTotalCents: discountedTotalCents });
    });

    tiers.sort(function (a, b) { return a.minQty - b.minQty; });
    return tiers;
  }

  function getVariantId(section) {
    var input = section.querySelector(CONFIG.variantIdSelector);
    return input ? String(input.value) : null;
  }

  // Variant's normal price in cents, from the most reliable source available.
  function getBaseUnitCents(variantId) {
    var bb = document.querySelector(CONFIG.buyButtonsDataSelector);
    if (bb && variantId) {
      try {
        var map = JSON.parse(bb.textContent);
        if (map[variantId] && typeof map[variantId].price === 'number') return map[variantId].price;
      } catch (e) { /* ignore */ }
    }
    var sv = document.querySelector(CONFIG.selectedVariantSelector);
    if (sv) {
      try {
        var variant = JSON.parse(sv.textContent);
        if (variant && typeof variant.price === 'number') return variant.price;
      } catch (e) { /* ignore */ }
    }
    try {
      var cfg = window.MultiscountConfig;
      for (var key in cfg) {
        if (!Object.prototype.hasOwnProperty.call(cfg, key)) continue;
        var vs = cfg[key] && cfg[key].currentProduct && cfg[key].currentProduct.variants;
        if (!vs) continue;
        for (var i = 0; i < vs.length; i++) {
          if (String(vs[i].id) === String(variantId) && typeof vs[i].price === 'number') return vs[i].price;
        }
      }
    } catch (e) { /* ignore */ }
    return null;
  }

  // null = below the first tier (normal price).
  function tierForQuantity(tiers, qty) {
    var match = null;
    for (var i = 0; i < tiers.length; i++) {
      if (qty >= tiers[i].minQty) match = tiers[i];
      else break;
    }
    return match;
  }

  // Prefer the exact per-unit price implied by the table's discounted total — its
  // price column now matches Shopify's checkout rounding. The displayed % is rounded,
  // so computing from it drifts, most visibly on higher-priced products. Fall back to
  // the % only when the total isn't available.
  function unitCentsForTier(baseCents, tier) {
    if (!tier) return baseCents;
    if (tier.discountedTotalCents != null && tier.minQty) return Math.round(tier.discountedTotalCents / tier.minQty);
    if (tier.pct != null) return Math.round(baseCents * (100 - tier.pct) / 100);
    return baseCents;
  }

  // Line total = per-unit price x qty, so it always matches the unit price shown
  function lineTotalCentsForTier(baseCents, tier, qty) {
    return unitCentsForTier(baseCents, tier) * qty;
  }

  function Controller(section) {
    this.section = section;
    this.tiers = [];
    this.baseCents = null;
    this.priceLabels = null;
    this.qtyInput = null;
    this.boundOnChange = null;
    this.boundOnClick = null;
    this.debouncedApply = debounce(this.apply.bind(this), CONFIG.debounceMs);
  }

  // Re-read the table and prices. Returns true once set up.
  Controller.prototype.refresh = function () {
    var table = getLatestTable(this.section);
    if (!table) { this.teardownListeners(); this.tiers = []; return false; }

    this.tiers = parseTiers(table);
    this.baseCents = getBaseUnitCents(getVariantId(this.section));
    this.priceLabels = null; // re-read from the (now pristine) price markup

    if (!this.tiers.length || this.baseCents == null) { this.teardownListeners(); return false; }

    this.bindQuantity();
    this.apply();
    this.syncButtonTotal();
    return true;
  };

  Controller.prototype.getQuantity = function () {
    if (!this.qtyInput) return 1;
    var v = parseInt(this.qtyInput.value, 10);
    return (!v || isNaN(v) || v < 1) ? 1 : v;
  };

  // Discounted line total for the current variant/qty, or null when there's no discount
  // Called by buy-buttons-pricing.js.
  Controller.prototype.lineTotalCents = function (qty) {
    if (!this.tiers.length || this.baseCents == null) return null;
    var tier = tierForQuantity(this.tiers, qty);
    return tier ? lineTotalCentsForTier(this.baseCents, tier, qty) : null;
  };

  Controller.prototype.bindQuantity = function () {
    var input = this.section.querySelector(CONFIG.quantityInputSelector);
    if (!input) { this.teardownListeners(); return; }
    if (input === this.qtyInput && this.boundOnChange) return; // already bound to this element

    this.teardownListeners();
    this.qtyInput = input;

    this.boundOnChange = this.debouncedApply;
    input.addEventListener('input', this.boundOnChange);
    input.addEventListener('change', this.boundOnChange);

    var self = this;
    this.boundOnClick = function () {
      window.requestAnimationFrame(function () { self.debouncedApply(); });
    };
    var buttons = this.section.querySelectorAll(CONFIG.quantityButtonsSelector);
    for (var i = 0; i < buttons.length; i++) buttons[i].addEventListener('click', this.boundOnClick);
    this.boundButtons = buttons;
  };

  Controller.prototype.teardownListeners = function () {
    if (this.qtyInput && this.boundOnChange) {
      this.qtyInput.removeEventListener('input', this.boundOnChange);
      this.qtyInput.removeEventListener('change', this.boundOnChange);
    }
    if (this.boundButtons && this.boundOnClick) {
      for (var i = 0; i < this.boundButtons.length; i++) this.boundButtons[i].removeEventListener('click', this.boundOnClick);
    }
    this.qtyInput = null;
    this.boundOnChange = null;
    this.boundOnClick = null;
    this.boundButtons = null;
  };

  Controller.prototype.apply = function () {
    if (!this.tiers.length || this.baseCents == null) return;
    var qty = this.getQuantity();
    this.applyUnitPrice(tierForQuantity(this.tiers, qty), qty);
  };

  // The unit price on the page. Targets whichever price element is actually visible 
  // includes visually-hidden price labels, so screen readers still get context.
  Controller.prototype.applyUnitPrice = function (tier) {
    var root = this.section.querySelector(CONFIG.priceRootSelector);
    if (!root) return;

    var onSale = root.classList.contains('f-price--on-sale');
    var container = root.querySelector(onSale ? '.f-price__sale' : '.f-price__regular');
    if (!container) return;

    if (this.priceLabels == null) this.priceLabels = getPriceLabels(root);

    if (container.getAttribute(CONFIG.markerAttr) == null) {
      container.setAttribute(CONFIG.markerAttr, container.innerHTML); // stash original to revert to
    }

    if (!tier) {
      var original = container.getAttribute(CONFIG.markerAttr);
      if (original != null && container.innerHTML !== original) container.innerHTML = original;
      return;
    }

    var labels = this.priceLabels;
    var unitCents = unitCentsForTier(this.baseCents, tier);

    var html = '';
    if (labels.sale) html += '<span class="visually-hidden visually-hidden--inline">' + labels.sale + '</span>';
    html += '<span class="f-price-item f-price-item--regular ms-live-price__now">' + formatMoney(unitCents) + '</span>';
    if (CONFIG.showOriginalStrikethrough && unitCents < this.baseCents) {
      if (labels.regular) html += ' <span class="visually-hidden visually-hidden--inline">' + labels.regular + '</span>';
      html += '<s class="ms-live-price__was">' + formatMoney(this.baseCents) + '</s>';
    }
    if (container.innerHTML !== html) container.innerHTML = html;
  };

  // Nudge buy-buttons-pricing.js to recompute the button total
  // Only needed when a discount is active — e.g. a variant
  // switch while a bulk quantity is selected; normal qty changes already fire it.
  Controller.prototype.syncButtonTotal = function () {
    if (!CONFIG.updateButtonTotal || !this.qtyInput) return;
    if (!tierForQuantity(this.tiers, this.getQuantity())) return;
    this.qtyInput.dispatchEvent(new Event('change', { bubbles: true }));
  };

  function init() {
    var sections = document.querySelectorAll(CONFIG.sectionSelector);
    if (!sections.length) return;

    sections.forEach(function (section) {
      if (!section.querySelector('form[data-type="add-to-cart-form"]')) return;

      var controller = new Controller(section);
      controllers.push(controller);
      var reinit = debounce(function () { controller.refresh(); }, CONFIG.reinitDebounceMs);

      // Re-run when the table appears or the section re-renders on a variant switch.
      var observer = new MutationObserver(function (mutations) {
        for (var i = 0; i < mutations.length; i++) {
          var added = mutations[i].addedNodes;
          for (var j = 0; j < added.length; j++) {
            var node = added[j];
            if (node.nodeType !== 1) continue;
            var isRelevant =
              (node.matches && (node.matches(CONFIG.tableInstanceSelector) || node.matches('table') || node.matches('.f-price'))) ||
              (node.querySelector && (
                node.querySelector(CONFIG.tableInstanceSelector) ||
                node.querySelector('table') ||
                node.querySelector('.f-price') || // price re-render on variant switch
                node.querySelector('form[data-type="add-to-cart-form"]')
              ));
            if (isRelevant) { reinit(); return; }
          }
        }
      });
      observer.observe(section, { childList: true, subtree: true });

      // The theme's own variant-change signal — the reliable trigger to re-read
      // the new variant's base price (the DOM observer alone can miss the swap).
      var sectionId = section.getAttribute('data-section');
      if (window.FoxTheme && FoxTheme.pubsub && FoxTheme.pubsub.PUB_SUB_EVENTS) {
        FoxTheme.pubsub.subscribe(FoxTheme.pubsub.PUB_SUB_EVENTS.variantChange, function (event) {
          var d = event && event.data;
          if (!d || (sectionId && String(d.sectionId) !== String(sectionId))) return;
          reinit();
        });
      }

      // Retry until the (async) table shows up, then stop.
      CONFIG.retryDelaysMs.forEach(function (delay) {
        setTimeout(function () { if (!controller.tiers.length) controller.refresh(); }, delay);
      });
    });
  }

  // buy-buttons-pricing.js calls this to get a discounted line total (or null).
  window.MultiscountTiered = window.MultiscountTiered || {};
  window.MultiscountTiered.lineTotalCents = function (variantId, qty) {
    for (var i = 0; i < controllers.length; i++) {
      var c = controllers[i];
      if (getVariantId(c.section) !== String(variantId)) continue;
      var total = c.lineTotalCents(qty);
      if (total != null) return total;
    }
    return null;
  };

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', init);
  } else {
    // fire away!
    init();
  }
})();
